/**
 * organization/utils/accessControl.ts
 *
 * Motor puro de control de acceso corporativo — E1.3.
 *
 * Jerarquía de ámbito:
 *   global   → director que ve todo el corporativo (todas las empresas)
 *   business → admin/gerente con acceso a una empresa y sus tiendas
 *   store    → cajero/vendedor/técnico limitado a su tienda asignada
 *
 * SIN React. SIN hooks. SIN Supabase. SIN Zustand.
 * Solo tipos e interfaces y funciones puras.
 */

import type { Role, Permission } from '@shared/types/roles.types'
import { ROLE_PERMISSIONS, ROLE_LEVEL } from '@shared/types/roles.types'

// ── Ámbito de acceso ───────────────────────────────────────────────────────

/**
 * Qué tan amplio es el acceso del usuario dentro del ecosistema CELMAXX.
 *   global   → ve todo (director)
 *   business → ve su empresa y todas sus tiendas (admin, gerente)
 *   store    → ve solo sus tiendas asignadas (encargado, cajero, vendedor, técnico)
 */
export type AccessScope = 'global' | 'business' | 'store'

// ── Sub-entidades de acceso ────────────────────────────────────────────────

export interface AccessibleBusiness {
  businessId: string
  role: string
  isPrimary: boolean
}

export interface AccessibleStore {
  storeId: string
  storeCode: string
  businessId: string | null
  role: string
  isPrimary: boolean
}

// ── Contexto de acceso mínimo ──────────────────────────────────────────────
// Las funciones puras aceptan AccessContext para evitar dependencia circular
// con ActiveCajero. ActiveCajero en useAuth.ts satisface esta forma por
// compatibilidad estructural de TypeScript.

export interface AccessContext {
  accessScope: AccessScope
  accessibleBusinesses: AccessibleBusiness[]
  accessibleStores: AccessibleStore[]
  extraPermissions: string[]
}

// ── Acceso a empresa ───────────────────────────────────────────────────────

export function canAccessBusiness(ctx: AccessContext, businessId: string): boolean {
  if (ctx.accessScope === 'global') return true
  return ctx.accessibleBusinesses.some(b => b.businessId === businessId)
}

// ── Acceso a tienda ───────────────────────────────────────────────────────

export function canAccessStore(ctx: AccessContext, storeId: string): boolean {
  if (ctx.accessScope === 'global') return true
  return ctx.accessibleStores.some(s => s.storeId === storeId)
}

// ── Administrar empresa ────────────────────────────────────────────────────

export function canManageBusiness(ctx: AccessContext, role: Role, businessId: string): boolean {
  if (!canAccessBusiness(ctx, businessId)) return false
  const perms = resolveEffectivePermissions(role, ctx.extraPermissions)
  return perms.has('administrar_empresas')
}

// ── Dashboard corporativo ──────────────────────────────────────────────────

export function canViewCorporateDashboard(ctx: AccessContext, role: Role): boolean {
  // Global scope = owner (SUPER USUARIO) — siempre ve el dashboard corporativo.
  if (ctx.accessScope === 'global') return true
  // Operativos (store) nunca ven el corporativo, aunque un permiso extra se cuele.
  if (ctx.accessScope === 'store') return false
  // Business scope (admin/director/gerente): requiere permiso corporativo.
  // admin/gerente traen 'ver_reportes_corporativos'; director 'ver_dashboard_corporativo'.
  const perms = resolveEffectivePermissions(role, ctx.extraPermissions)
  return perms.has('ver_dashboard_corporativo') || perms.has('ver_reportes_corporativos')
}

// ── Gestionar licencias ────────────────────────────────────────────────────

export function canManageLicenses(ctx: AccessContext, role: Role): boolean {
  const perms = resolveEffectivePermissions(role, ctx.extraPermissions)
  return perms.has('gestionar_licencias')
}

// ── Gestionar usuarios ─────────────────────────────────────────────────────

export function canManageUsers(ctx: AccessContext, role: Role): boolean {
  const perms = resolveEffectivePermissions(role, ctx.extraPermissions)
  return perms.has('gestion_usuarios')
}

// ── Cambiar tienda activa ─────────────────────────────────────────────────

export function canSwitchStore(ctx: AccessContext, role: Role): boolean {
  const level = ROLE_LEVEL[role] ?? 1
  // Directores (nivel ≥ 6) siempre pueden cambiar — el cache stale puede mostrar length=1
  if (level < 6 && ctx.accessibleStores.length <= 1) return false
  const perms = resolveEffectivePermissions(role, ctx.extraPermissions)
  return perms.has('cambiar_tienda_activa')
}

// ── Tienda primaria ───────────────────────────────────────────────────────

export function getPrimaryStore(
  ctx: AccessContext,
  primaryStoreId: string | null
): AccessibleStore | null {
  if (primaryStoreId) {
    return ctx.accessibleStores.find(s => s.storeId === primaryStoreId) ?? null
  }
  return ctx.accessibleStores.find(s => s.isPrimary) ?? ctx.accessibleStores[0] ?? null
}

// ── IDs de tiendas accesibles ─────────────────────────────────────────────

export function getAccessibleStoreIds(ctx: AccessContext): string[] {
  return ctx.accessibleStores.map(s => s.storeId)
}

// ── IDs de empresas accesibles ────────────────────────────────────────────

export function getAccessibleBusinessIds(ctx: AccessContext): string[] {
  return ctx.accessibleBusinesses.map(b => b.businessId)
}

// ── Resolver permisos efectivos ───────────────────────────────────────────
// Combina los permisos del rol base con permisos adicionales de access rows.
// Los extras solo SUMAN — no pueden quitar permisos del rol base (por ahora).

export function resolveEffectivePermissions(
  role: Role,
  extraPermissions: string[] = []
): Set<Permission> {
  const base: Permission[] = ROLE_PERMISSIONS[role] ?? []
  const extras = extraPermissions as Permission[]
  return new Set([...base, ...extras])
}

// ── Resolver AccessScope ──────────────────────────────────────────────────
// Jerarquía real (alineada con is_owner / user_accessible_business_ids del backend):
//   - owner (SUPER USUARIO)  → 'global' (ve TODO el ecosistema)
//   - director               → 'business' (corporativo ACOTADO a su alcance, NO global)
//   - gerente/supervisor con ≥1 empresa → 'business'
//   - resto                  → 'store'
// Antes: cualquier director era 'global' (veía todas las empresas) e is_owner
// no se consultaba — un owner no-director no era global y un director no-owner
// veía de más. Ahora SOLO el owner es global.

export function resolveAccessScope(
  role: Role,
  accessibleBusinesses: AccessibleBusiness[],
  isOwner = false
): AccessScope {
  if (isOwner) return 'global' // SUPER USUARIO: todo el ecosistema
  const level = ROLE_LEVEL[role] ?? 1
  if (level >= 6) return 'business' // director: corporativo acotado a su alcance
  if (level >= 4 && accessibleBusinesses.length >= 1) return 'business' // gerente/supervisor
  return 'store'
}

// ── Modo corporativo ───────────────────────────────────────────────────────
// Quién puede entrar a la Vista Corporativa (supervisión, NO operación).
// Alcance global (director) o business (admin/gerente). Los usuarios de scope
// 'store' operan una tienda pero no supervisan el corporativo.

export function isCorporateScope(accessScope: AccessScope): boolean {
  return accessScope === 'global' || accessScope === 'business'
}

// ── Construir AccessibleStore desde users.store_id (fallback legacy) ──────
// Usado cuando user_store_access no existe o no tiene datos.

export function buildLegacyAccessibleStore(
  storeId: string,
  storeCode: string,
  businessId: string | null,
  role: string
): AccessibleStore {
  return { storeId, storeCode, businessId, role, isPrimary: true }
}

// ── Construir AccessibleBusiness desde businesses.id (fallback legacy) ────
// Usado cuando user_business_access no existe o no tiene datos.

export function buildLegacyAccessibleBusiness(
  businessId: string,
  role: string
): AccessibleBusiness {
  return { businessId, role, isPrimary: true }
}

// ── Validar que usuario inactivo no obtenga acceso ────────────────────────

export function isUserActive(active: boolean): boolean {
  return active === true
}
